import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Dropdown,
  DropdownButton,
  Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  HeaderDisplayKeysWithType,
  EditableTableDiv,
} from '../../../components';
import { useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeat, UserInfo } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import { sightDiag } from '../../../utils/sight';
import {
  SheetHeaderDisplay,
  downloadDataAsExcel,
  printDataAsTable,
} from '../../../utils';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
type PageData = StudentGradeNoSeat & Sight & { diag: string };

function boolRender(v: unknown) {
  return v ? '是' : '否';
}
const manageIDMap: Record<string, string> = {
  '1': '長效散瞳劑\n(阿托平 Atropine)',
  '2': '短效散瞳劑',
  '3': '其他藥物',
  '4': '配鏡矯治',
  '5': '更換鏡片',
  '6': '遮眼治療',
  '7': '配戴隱形眼鏡',
  '8': '角膜塑型片',
  '9': '視力保健衛教',
  '10': '其他',
  '11': '定期檢查',
};
const formatSightValue = (value: unknown) => {
  if (typeof value !== 'number') return value as string;

  if (value === -1) return '<0.1';
  if (value < -1) return String(value);

  return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
  HeaderDisplayKeysWithType<PageData>)[] = [
    { display: '年級', property: 'grade' },
    {
      display: '班級',
      property: 'className',
      onRender: (value: unknown) => {
        const num = parseInt(value as string, 10);
        return !isNaN(num) && num > 100 && num < 200 ? num - 100 : value;
      },
      onSheetRender: (value) => {
        const num = parseInt(value as string, 10);
        if (!isNaN(num)) {
          return num > 100 && num < 200 ? num - 100 : num;
        }
        return value?.toString() ?? '';
      },
    },
    { display: '座號', property: 'seat' },
    { display: '學生', property: 'name' },
    {
      display: '性別',
      property: 'sex',
      onRender: (v) => (v === '1' ? '男' : '女'),
      onSheetRender: (v) => (v === '1' ? '男' : '女'),
    },
    {
      display: '散瞳治療',
      property: 'isDilated',
      onRender: boolRender,
      onSheetRender: boolRender,
    },
    {
      display: '裸視右',
      property: 'sight0R',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '裸視左',
      property: 'sight0L',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡右',
      property: 'sightR',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡左',
      property: 'sightL',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '散瞳',
      property: 'isDilating',
      onRender: boolRender,
      onSheetRender: boolRender,
    },
    { display: '近視右', property: 'eNearR' },
    { display: '近視左', property: 'eNearL' },
    { display: '遠視右', property: 'eFarR' },
    { display: '遠視左', property: 'eFarL' },
    { display: '散光右', property: 'eSanR' },
    { display: '散光左', property: 'eSanL' },
    { display: '屈光右', property: 'diopR' },
    { display: '屈光左', property: 'diopL' },
    { display: '診斷', property: 'diag' },
    { display: '備註', property: 'eSight99State' },
    {
      display: '處置代號',
      property: 'sightManageCodes',
      onRender: (v) => (Array.isArray(v) ? v.join(',') : v),
      onSheetRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
    },
    {
      display: '醫師建議處置',
      property: 'sightManageCodes',
      onRender: (v) => {
        if (Array.isArray(v)) {
          return v.map(id => manageIDMap[id] || '').join(', ');
        }
        return '';
      },
      onSheetRender: (v) => {
        if (Array.isArray(v)) {
          return v.map(id => manageIDMap[id] || '').join(', ');
        }
        return '';
      },
    },
    {
      display: '定期檢查', property: 'periodical',
      onRender: (v) => {
        return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
      },
      onSheetRender: (v) => {
        return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : "";
      },
    },
  ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const sightLongDilation: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const { yearSem, element } = useSemGrade();
  const [students, setStudents] = useState<PageData[]>([]);
  const [userinfo, setUser] = useState<UserInfo>();
  useEffect(() => {
    if (yearSem) {
      apis.getUserInfo()
        .then((u) => {
          setUser(u);
        })
        .catch(catchErrorForModal);
      toast
        .promise(
          apis
            .getSightLongDilation(yearSem.year, yearSem.sem)
            .then(r => {
              setStudents(
                r.map(({ sight, ...s }) => ({
                  ...sight,
                  ...s,
                  diag: sightDiag(sight),
                }))
              )
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error: '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);
    }
  }, [yearSem?.year, yearSem?.sem]);

  return (
    <AuthedLayout>
      <Row className="justify-content-between">
        <span className="mr-3">{element}</span>
        <DropdownButton
          id="sight-long-dilation-export"
          title="匯出"
          variant="success"
          disabled={!students.length}
        >
          <Dropdown.Item
            onClick={() => {
              downloadDataAsExcel({
                title:
                  `${user.schools[user.currentSchool].name}` +
                  `${yearSem?.year}學年` +
                  `${yearSem?.sem}學期_處置長效型散瞳劑清單`,
                values: students,
                headers: tableHeader,
                context: SightContext,
                footer:
                  '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                exportfile: userinfo?.exportFileType,
              });
            }}
          >
            Excel 下載
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => {
              printDataAsTable({
                title:
                  `${user.schools[user.currentSchool].name}` +
                  `${yearSem?.year}學年` +
                  `${yearSem?.sem}學期_處置長效型散瞳劑清單`,
                values: students,
                headers: tableHeader,
                context: SightContext,
                footer:
                  '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
              });
            }}
          >
            列印
          </Dropdown.Item>
        </DropdownButton>
      </Row>
      <hr />
      <Row>
        <EditableTableDiv
          headers={tableHeader}
          values={students}
          context={SightContext}
        />
      </Row>
    </AuthedLayout>
  );
};

export const SightLongDilation = connector(sightLongDilation);
